/**
 * The sokoban rules, ported from `apps/puzzletypes/sokoban/board.py`. The two are kept at
 * parity by the shared fixtures in `shared/fixtures/sokoban/`.
 *
 * This file knows nothing of React, of the core, or of the solution: it reads rows and moves
 * and returns positions. It is what the Player replays on every key press, so it stays pure.
 */
import {
  BOX,
  BOX_ON_GOAL,
  FLOOR,
  GOAL,
  PLAYER,
  PLAYER_ON_GOAL,
  WALL,
  type Direction,
} from "./types";

/** Characters XSB files use for a plain floor, all folded to a space. */
const FLOOR_ALIASES = new Set([" ", "-", "_"]);

export const DELTAS: Record<Direction, [number, number]> = {
  u: [-1, 0],
  d: [1, 0],
  l: [0, -1],
  r: [0, 1],
};

export type Cell = [row: number, col: number];

/** Cells live in sets, so they travel as `row,col` keys. */
export function key(row: number, col: number): string {
  return `${row},${col}`;
}

export function fromKey(value: string): Cell {
  const [row, col] = value.split(",").map(Number);
  return [row, col];
}

export interface Board {
  rows: number;
  cols: number;
  walls: Set<string>;
  goals: Set<string>;
}

export interface State {
  player: Cell;
  boxes: Set<string>;
}

/** Fold the floor aliases to spaces and pad every row to the widest one. */
export function normalizeRows(rows: string[]): string[] {
  const folded = rows.map((row) =>
    Array.from(row, (char) => (FLOOR_ALIASES.has(char) ? FLOOR : char)).join(""),
  );
  const width = folded.reduce((max, row) => Math.max(max, row.length), 0);
  return folded.map((row) => row.padEnd(width, FLOOR));
}

export function isWall(board: Board, [r, c]: Cell): boolean {
  return board.walls.has(key(r, c));
}

export function inside(board: Board, [r, c]: Cell): boolean {
  return r >= 0 && c >= 0 && r < board.rows && c < board.cols;
}

/**
 * Read rows into a board and its starting state. Unknown characters are read as floor: the
 * JSON Schema rejects them long before here. A board with no player has `player: null`.
 */
export function parseBoard(rows: string[]): { board: Board; state: State | null } {
  const grid = normalizeRows(rows);
  const walls = new Set<string>();
  const goals = new Set<string>();
  const boxes = new Set<string>();
  let player: Cell | null = null;
  grid.forEach((row, r) => {
    Array.from(row).forEach((char, c) => {
      if (char === WALL) {
        walls.add(key(r, c));
        return;
      }
      if (char === GOAL || char === BOX_ON_GOAL || char === PLAYER_ON_GOAL) goals.add(key(r, c));
      if (char === BOX || char === BOX_ON_GOAL) boxes.add(key(r, c));
      if ((char === PLAYER || char === PLAYER_ON_GOAL) && player === null) player = [r, c];
    });
  });
  const board: Board = { rows: grid.length, cols: grid[0]?.length ?? 0, walls, goals };
  return { board, state: player === null ? null : { player, boxes } };
}

/** One move, or `null` when a wall, the edge or a blocked box forbids it. */
export function step(
  board: Board,
  state: State,
  direction: string,
): { state: State; pushed: boolean } | null {
  const delta = DELTAS[direction.toLowerCase() as Direction];
  if (!delta) return null;
  const target: Cell = [state.player[0] + delta[0], state.player[1] + delta[1]];
  if (!inside(board, target) || isWall(board, target)) return null;
  if (!state.boxes.has(key(...target)))
    return { state: { player: target, boxes: state.boxes }, pushed: false };
  const beyond: Cell = [target[0] + delta[0], target[1] + delta[1]];
  if (!inside(board, beyond) || isWall(board, beyond) || state.boxes.has(key(...beyond)))
    return null;
  const boxes = new Set(state.boxes);
  boxes.delete(key(...target));
  boxes.add(key(...beyond));
  return { state: { player: target, boxes }, pushed: true };
}

export interface Replay {
  state: State;
  /** The same moves in canonical LURD case: upper for a push, lower for a walk. */
  moves: string;
  /** Index of the first move that could not be played, or `null` when all were legal. */
  illegalAt: number | null;
  pushes: number;
}

/** Play a move string from `start`, stopping at the first illegal move. */
export function replay(board: Board, start: State, moves: string): Replay {
  let state = start;
  let pushes = 0;
  const played: string[] = [];
  for (let index = 0; index < moves.length; index += 1) {
    const char = moves[index];
    const outcome = DELTAS[char.toLowerCase() as Direction] ? step(board, state, char) : null;
    if (!outcome) return { state, moves: played.join(""), illegalAt: index, pushes };
    state = outcome.state;
    if (outcome.pushed) pushes += 1;
    played.push(outcome.pushed ? char.toUpperCase() : char.toLowerCase());
  }
  return { state, moves: played.join(""), illegalAt: null, pushes };
}

/** Every goal covered. Boxes and goals are equal in number, so this is symmetric. */
export function isSolved(board: Board, state: State): boolean {
  if (state.boxes.size !== board.goals.size) return false;
  for (const box of state.boxes) if (!board.goals.has(box)) return false;
  return true;
}

/** How many boxes already sit on a goal — a fact the player reads off the board. */
export function boxesHome(board: Board, state: State): number {
  let count = 0;
  for (const box of state.boxes) if (board.goals.has(box)) count += 1;
  return count;
}

/**
 * Cells the player could ever stand on, boxes ignored. Ignoring boxes over-approximates: a
 * cell outside this area is unreachable whatever the player does, while a cell inside may
 * still be blocked today.
 */
export function reachable(board: Board, start: Cell): Set<string> {
  const seen = new Set<string>();
  const stack: Cell[] = [start];
  while (stack.length > 0) {
    const cell = stack.pop() as Cell;
    if (seen.has(key(...cell)) || !inside(board, cell) || isWall(board, cell)) continue;
    seen.add(key(...cell));
    for (const [dr, dc] of Object.values(DELTAS)) stack.push([cell[0] + dr, cell[1] + dc]);
  }
  return seen;
}

/** Whether the player's area touches the edge, i.e. the board is not walled in. */
export function escapes(board: Board, area: Set<string>): boolean {
  for (const cell of area) {
    const [r, c] = fromKey(cell);
    if (r === 0 || c === 0 || r === board.rows - 1 || c === board.cols - 1) return true;
  }
  return false;
}

/**
 * Non-goal floor cells wedged between two perpendicular walls. A box there can never move
 * again, so a box on one is a lost position — the only deadlock this type claims, and one
 * the walls alone prove.
 */
export function deadCorners(board: Board): Set<string> {
  const dead = new Set<string>();
  const blocked = (cell: Cell) => !inside(board, cell) || isWall(board, cell);
  for (let r = 0; r < board.rows; r += 1) {
    for (let c = 0; c < board.cols; c += 1) {
      if (isWall(board, [r, c]) || board.goals.has(key(r, c))) continue;
      const vertical = blocked([r - 1, c]) || blocked([r + 1, c]);
      const horizontal = blocked([r, c - 1]) || blocked([r, c + 1]);
      if (vertical && horizontal) dead.add(key(r, c));
    }
  }
  return dead;
}

/** The board as XSB rows again, with the boxes and the player where they now are. */
export function render(board: Board, state: State | null): string[] {
  const rows: string[] = [];
  for (let r = 0; r < board.rows; r += 1) {
    let row = "";
    for (let c = 0; c < board.cols; c += 1) {
      const goal = board.goals.has(key(r, c));
      if (isWall(board, [r, c])) row += WALL;
      else if (state && state.player[0] === r && state.player[1] === c)
        row += goal ? PLAYER_ON_GOAL : PLAYER;
      else if (state?.boxes.has(key(r, c))) row += goal ? BOX_ON_GOAL : BOX;
      else row += goal ? GOAL : FLOOR;
    }
    rows.push(row);
  }
  return rows;
}

/** How many players, boxes and goals the rows declare, whatever their arrangement. */
export function countChars(rows: string[]): { players: number; boxes: number; goals: number } {
  const joined = normalizeRows(rows).join("");
  const count = (char: string) => Array.from(joined).filter((c) => c === char).length;
  return {
    players: count(PLAYER) + count(PLAYER_ON_GOAL),
    boxes: count(BOX) + count(BOX_ON_GOAL),
    goals: count(GOAL) + count(BOX_ON_GOAL) + count(PLAYER_ON_GOAL),
  };
}
